/**
 * 💬 업데이트 문구 목록 창 — 보기(영업 이상) · 고치기(경영지원·관리자) (v2.216.0 · 2026-09-29)
 *
 * 사용자 요청: 「이 목록을 포털에 보관해주고 … 대분류로 abcde중 고르고 안에 맨트 골라서 활용할수 있게」
 *
 * 글 한 덩이를 통째로 고친다 — 표가 아니라 글이라 줄을 붙이고 빼기 쉽다:
 *   '#' 줄 = 대분류(「# A. 예산」) · 한 줄 = 문구 하나 「한글 사유 / English」 · 맨 앞 ● = 범용형.
 * 저장하면 **서버가 나눠 준 묶음**이 곧바로 고르는 칸(DealUpdatePhrasePicker)에 반영된다(같은 질의 열쇠).
 * 기본 글과 똑같이 저장하면 빈 칸으로 보낸다 = 「기본 목록 사용 중」으로 돌아간다(만료 안내 문구와 같은 규칙 · v2.153.0).
 * ⛔배경(바깥) 클릭·ESC 로 닫지 않는다 — 적던 글이 사라진다(check:modal).
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/deal-registration-api';
import { LoadingLines } from './ui/Skeleton';

/** 고르는 칸(DealUpdatePhrasePicker)과 같은 열쇠 — 저장하면 그쪽도 바로 바뀐다 */
export const UPDATE_PHRASES_QUERY_KEY = ['deals', 'updatePhrases'] as const;

export function DealUpdatePhrasesModal({ onClose }: { onClose: () => void }): React.JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: UPDATE_PHRASES_QUERY_KEY, queryFn: api.getUpdatePhrases });
  const d = q.data;

  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** 불러온 뒤 한 번만 칸을 채운다 — 이후에는 사람이 고친 값을 덮지 않는다 */
  const loadedRef = useRef(false);
  /** 열었을 때 글 — 닫기 전에 「바뀌었나」를 잰다 */
  const baseRef = useRef('');

  useEffect(() => {
    if (!d || loadedRef.current) return;
    loadedRef.current = true;
    setText(d.text);
    baseRef.current = d.text;
  }, [d]);

  const canEdit = d?.canEdit ?? false;
  const dirty = loadedRef.current && text !== baseRef.current;
  const phraseCount = (d?.groups ?? []).reduce((n, g) => n + g.phrases.length, 0);

  const save = useMutation({
    mutationFn: () => {
      const backToDefault = !!d && text.replace(/\r\n/g, '\n').trim() === d.defaultText.trim();
      return api.saveUpdatePhrases(backToDefault ? '' : text);
    },
    onSuccess: (r) => {
      setError(null);
      baseRef.current = r.text;
      setText(r.text);
      qc.setQueryData(UPDATE_PHRASES_QUERY_KEY, r);
      const n = r.groups.reduce((s, g) => s + g.phrases.length, 0);
      alert(`문구 목록을 저장했습니다 — 대분류 ${r.groups.length}개 · 문구 ${n}개.\n이제 「💬 문구 고르기」 칸에 이 목록이 나옵니다.`);
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const close = (): void => {
    if (dirty && !confirm('고친 글이 있습니다.\n닫으면 저장하지 않은 내용이 사라집니다.\n\n그래도 닫을까요?')) return;
    onClose();
  };

  const resetToDefault = (): void => {
    if (!d) return;
    if (!confirm('처음 기본 목록(6분류 · 50문구)으로 되돌립니다.\n(저장을 눌러야 실제로 바뀝니다)')) return;
    setText(d.defaultText);
  };

  // z-[90] — [수정] 창(z-80)·상세 창(z-70) 안에서 열리므로 그 위에
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-xl w-[860px] max-w-[97vw] max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h3 className="ui-subsection-title">💬 업데이트 문구 목록</h3>
            {d && (
              <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${
                d.isDefault ? 'bg-slate-100 text-slate-600 border-slate-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                {d.isDefault ? '기본 목록 사용 중' : `고친 목록 사용 중 (${d.savedAt} 저장)`}
                {' · '}대분류 {d.groups.length}개 · 문구 {phraseCount}개
              </span>
            )}
            {!canEdit && d && (
              <span className="text-xs text-amber-700">보기만 할 수 있습니다 — 고치기는 경영지원·관리자</span>
            )}
          </div>
          <button type="button" onClick={close} aria-label="닫기"
            className="ui-close">×</button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-3">
          <p className="text-xs text-slate-500 leading-5">
            딜등록 업데이트를 적을 때 「💬 문구 고르기」 칸에 나오는 목록입니다. 고른 문구는 칸에 글자로 들어갈 뿐이라
            넣은 뒤 마음대로 고쳐 저장하면 됩니다. 인포트렌드 안내 메일에 그대로 실리는 글이므로 <b className="text-slate-700">한글 / 영문</b> 을 짝으로 적어 두세요.
          </p>
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2 leading-5">
            <b>적는 법</b> — <code className="bg-white border border-slate-200 rounded px-1"># A. 예산</code> 처럼 <b>#</b> 으로 시작하는 줄이 대분류(글자가 고르는 칸의 이름)이고,
            그 아래 한 줄이 문구 하나입니다. <code className="bg-white border border-slate-200 rounded px-1">한글 사유 / English</code> 모양으로,
            맨 앞에 <b>●</b> 를 붙이면 「어느 딜에나 무난한 범용형」 표시가 됩니다. 문구가 없는 대분류는 나오지 않습니다.
          </div>

          {error && (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2 whitespace-pre-line">{error}</div>
          )}

          {q.isLoading ? (
            <div className="text-sm text-slate-400 py-8 text-center"><LoadingLines /></div>
          ) : q.isError ? (
            <div className="text-sm text-rose-600 py-8 text-center">목록을 불러오지 못했습니다 — {(q.error as Error).message}</div>
          ) : (
            <textarea
              value={text}
              rows={22}
              maxLength={20000}
              disabled={!canEdit}
              spellCheck={false}
              onChange={(e) => setText(e.target.value)}
              className="ui-input ui-textarea w-full font-mono"
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b">
          <div className="text-xs text-slate-500">
            {canEdit ? (dirty ? '고친 글이 있습니다 — 저장을 눌러야 반영됩니다' : '') : ''}
          </div>
          <div className="flex items-center gap-2">
            {canEdit && d && (
              <button type="button" onClick={resetToDefault}
                className="btn btn-secondary">
                ↺ 기본 목록으로
              </button>
            )}
            <button type="button" onClick={close}
              className="btn btn-secondary">
              닫기
            </button>
            {canEdit && (
              <button type="button" disabled={!dirty || save.isPending} onClick={() => save.mutate()}
                className="btn btn-primary">
                {save.isPending ? '저장 중…' : '저장'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
